'use client'

import { usePointOfSaleNameFormatter } from '@/feature/point-of-sale/hooks/usePointOfSaleNameFormatter.client'
import { createPointOfSaleDrawerOpenIntent } from '@/feature/point-of-sale/services/pointOfSale.intents'
import {
  closePointOfSaleDrawerAtom,
  openSelectPickupPointOfSaleDrawerAtom,
  pointOfSaleDrawerAtom,
  updateOpenPointOfSaleIdAtom,
} from '@/feature/point-of-sale/services/pointOfSale.store'
import { useTranslation } from '@/lib/i18n/useTranslation.client'
import { pushToGtmDataLayer } from '@/lib/tracking/services/tracking.gtm'
import { Drawer, DrawerAction, DrawerBody, DrawerHeading } from '@/ui/drawer/drawer/Drawer.client'
import { Icon } from '@/ui/icons/icon/Icon'
import { useLazyQuery } from '@apollo/client'
import { gql } from '@gql-generated/gql'
import { PointOfSaleDrawerDocument } from '@gql-generated/graphql'
import { useAtomValue, useSetAtom } from 'jotai'
import moment from 'moment'
import { useEffect } from 'react'
import { PointOfSaleOpeningStatus } from '../../point-of-sale-opening-status/PointOfSaleOpeningStatus.client'
import { DisplayPointOfSaleInfos } from './_components/DisplayPointOfSaleInfos.client'

gql(/* GraphQL */ `
  query PointOfSaleDrawer($id: String!) {
    pointOfSale(pointOfSaleId: $id) {
      ...DisplayPointOfSaleInfos_pointOfSale
    }
  }
`)

gql(/* GraphQL */ `
  query GetPointOfSaleDrawerConfig {
    config {
      pointOfSale {
        isFavoritePointOfSaleEnabled
        isPickupPointOfSaleEnabled
      }
    }
  }
`)

export function PointOfSaleDrawer() {
  const { t } = useTranslation(['generic', 'pointOfSale', 'pointOfSaleStatusCard'])
  const formatPointOfSaleName = usePointOfSaleNameFormatter()

  const { isOpen, pointOfSaleId, changeStoreAllowed } = useAtomValue(pointOfSaleDrawerAtom)

  const closeDrawer = useSetAtom(closePointOfSaleDrawerAtom)

  const [loadPointOfSale, { data, loading }] = useLazyQuery(PointOfSaleDrawerDocument, {
    variables: { id: pointOfSaleId! },
  })

  useEffect(() => {
    if (pointOfSaleId && isOpen && pointOfSaleId) {
      loadPointOfSale({ variables: { id: pointOfSaleId! } })
    }
  }, [loadPointOfSale, pointOfSaleId, isOpen])

  const updateOpenPointOfSaleId = useSetAtom(updateOpenPointOfSaleIdAtom)

  const pointOfSale = data?.pointOfSale

  const dayOpeningHours = pointOfSale?.openingHours?.find(
    (dayOpeningHours) => dayOpeningHours.dayOfWeek === moment().isoWeekday(),
  )

  const openSelectPickupPointOfSaleDrawer = useSetAtom(openSelectPickupPointOfSaleDrawerAtom)

  return (
    <Drawer
      title={pointOfSale ? formatPointOfSaleName(pointOfSale) : undefined}
      aria-label={loading || !pointOfSale ? t('pointOfSaleStatusCard:myPointOfSale') : undefined}
      upperCaseTitle
      isOpen={isOpen}
      onOpenChange={closeDrawer}
      isLoading={loading}
    >
      <DrawerHeading>
        <div className="space-y-1">
          {pointOfSale?.name && <p className="font-bold">{pointOfSale.name}</p>}
          {dayOpeningHours && (
            <PointOfSaleOpeningStatus
              openingHours={dayOpeningHours}
              exceptionalDates={pointOfSale?.exceptionalDates || []}
            />
          )}
        </div>
      </DrawerHeading>

      <DrawerBody>
        <DisplayPointOfSaleInfos infos={pointOfSale} />
      </DrawerBody>

      {changeStoreAllowed && (
        <DrawerAction
          type="custom"
          handle="change-store"
          action={async () => {
            if (!pointOfSaleId) {
              console.error('No point of sale id defined')

              return
            }

            openSelectPickupPointOfSaleDrawer({
              selectedId: pointOfSaleId,
              onClose: (selectedPointOfSaleId: string) => {
                updateOpenPointOfSaleId(selectedPointOfSaleId)

                pushToGtmDataLayer({
                  event: 'gaEvent',
                  eventCategory: 'magasin favori',
                  eventAction: 'changement - tableau de bord',
                  eventLabel: `confirmation - ${pointOfSaleId} | ${selectedPointOfSaleId}`,
                })
              },
            })

            pushToGtmDataLayer({
              event: 'gaEvent',
              eventCategory: 'header',
              eventAction: 'drawer magasin',
              eventLabel: 'changer de magasin',
            })

            return false
          }}
          icon={<Icon.Store />}
          variant="secondary"
          intent={createPointOfSaleDrawerOpenIntent()}
        >
          {t('pointOfSale:changeStore')}
        </DrawerAction>
      )}
    </Drawer>
  )
}
